import {ConnectedPlatforms, ExternalAuths, PlatformOptions, PosaPlatforms, Wallets} from './types';

// 16 chars xxxx-xxxx-xxxx-xxxx
export const PosaCodePattern = /^[A-Z|a-z|0-9|-]{16,20}$/;

export const isPosaCodeValid = (code) => PosaCodePattern.test(code);

export const getPlatformLangCode = (platform: string) => {
  switch (platform) {
    case 'pc':
      return 'epic.code.redemption.pcMac';
    case 'psn':
      return 'epic.code.redemption.psn';
    case 'mobile':
      return 'epic.code.redemption.mobile';
    case 'xbl':
      return 'epic.code.redemption.xbl';
    case 'nintendo':
      return 'epic.code.redemption.nintendo';
    default:
    // no-op
  }
  return null;
};

export const getPlatformShortToLong = (platform: string): PosaPlatforms => {
  switch (platform) {
    case 'pc':
      return PosaPlatforms.EPIC;
    case 'mobile':
      return PosaPlatforms.MOBILE;
    case 'psn':
      return PosaPlatforms.PSN;
    case 'xbl':
      return PosaPlatforms.XBL;
    case 'nintendo':
      return PosaPlatforms.SWITCH;
    default:
      return PosaPlatforms.EPIC;
  }
};

export const getPlatformLongToShort = (platform: PosaPlatforms) => {
  switch (platform) {
    case PosaPlatforms.EPIC:
      return 'pc';
    case PosaPlatforms.PSN:
      return 'psn';
    case PosaPlatforms.MOBILE:
      return 'mobile';
    case PosaPlatforms.XBL:
      return 'xbl';
    case PosaPlatforms.SWITCH:
      return 'nintendo';
    default:
    // no-op
  }
  return null;
};

export const getLocalizedPlatformName = (
  platform: string,
  localizedMessage: (code: string) => string
): string => {
  const msgCode = getPlatformLangCode(platform);
  if (!msgCode) return '';
  return localizedMessage(msgCode) || '';
};

export const hasExternalAccount = (platform: string, externalAuths: ExternalAuths[]) => {
  for (const external of externalAuths) {
    if (external.type === platform) return true;
  }
  return false;
};

export const isPlatformDisabled = (platform: string, connectedPlatforms: ConnectedPlatforms[]) => {
  for (const connectedPlatform of connectedPlatforms) {
    if (getPlatformLongToShort(connectedPlatform.platform) === platform)
      return connectedPlatform.platformDisabled;
  }
  return false;
};

export const validConnectedAccount = (
  platform: PosaPlatforms,
  connectedPlatforms: ConnectedPlatforms[]
): boolean => {
  if (['pc', 'mobile'].includes(platform)) return true;
  for (const account of connectedPlatforms) {
    if (account.platform === platform && account.redeemable) return true;
  }
  return false;
};

/**
 * Generate platform options based on the externalAuths availabe
 * @param {*} externalAuths externalAuths from PosaRedeemStore
 * @param {*} message message func that will return the correct Label locale
 */
export const generatePlatformOptions = (
  externalAuths: ExternalAuths[],
  localizedMessage: (code: string) => string,
  connectedPlatforms: ConnectedPlatforms[]
) => {
  if (!externalAuths) throw new Error('generatePlatformOptions missing params');
  const defaults = [
    {
      label: getLocalizedPlatformName('pc', localizedMessage),
      value: 'pc',
      disabled: false,
    },
    {
      label: getLocalizedPlatformName('psn', localizedMessage),
      value: 'psn',
    },
    {
      label: getLocalizedPlatformName('mobile', localizedMessage),
      value: 'mobile',
      disabled: false,
    },
    {
      label: getLocalizedPlatformName('xbl', localizedMessage),
      value: 'xbl',
    },
    {
      label: getLocalizedPlatformName('nintendo', localizedMessage),
      value: 'nintendo',
    },
  ];
  return defaults
    .map((item) => {
      const clone: PlatformOptions = {...item};
      const platform = clone.value;
      // if disabled property is not pre defined (PC and Mobile),
      // we consider it will always be eligit for user to redeem
      clone.platformDisabled = isPlatformDisabled(platform, connectedPlatforms);
      clone.hasExternalAuth = true;
      if (clone.disabled === undefined) {
        clone.hasExternalAuth = hasExternalAccount(platform, externalAuths);
        clone.disabled = !clone.hasExternalAuth || clone.platformDisabled;
      }
      return clone;
    })
    .sort((a, b) => {
      if (a.disabled === b.disabled) {
        return a.value < b.value ? -1 : 0;
      }
      if (a.disabled) return 1;
      return -1;
    });
};

export const regionBlockedAccounts = (
  connectedPlatforms: ConnectedPlatforms[]
): ConnectedPlatforms[] => connectedPlatforms.filter((account) => !account.redeemable);

/**
 * get epicAccountName for each platform
 * @param {*} platform
 * @param {*} externalAuths
 */
export const getDisplayNameForPlatform = (platform, externalAuths) => {
  let displayName = null;
  try {
    for (const auth of externalAuths) {
      if (auth.type === platform) {
        displayName = auth.externalDisplayName || null;
        break;
      }
    }
  } catch (ex) {
    // no-op
  }

  return displayName;
};

export const getCardValue = (offer) => {
  let value = null;
  try {
    value = offer.customAttributes.VBucks_Value.value;
  } catch (ex) {
    // no-op
  }

  return value;
};
export const getWalletId = (platform: string): string => {
  switch (platform) {
    case 'pc':
    case 'mobile':
      return 'EPIC';
    case 'psn':
      return 'SONY';
    case 'nintendo':
      return 'NINTENDO';
    case 'xbl':
      return 'XBOX_LIVE';
    default:
    // no-op
  }
  return '';
};

export const getBalance = (wallets: Wallets[], platform: string): string | null => {
  const walletId = getWalletId(platform);
  try {
    if (walletId) {
      for (const wallet of wallets) {
        if (wallet.walletId === walletId) {
          return wallet.balance;
        }
      }
    }
  } catch (ex) {
    // no-op
  }
  return null;
};

export const getCardImage = (offer) => {
  let image = null;
  try {
    const {keyImages} = offer;
    for (const keyImage of keyImages) {
      if (keyImage.type === 'POSA') {
        image = keyImage.url;
        break;
      }
    }
  } catch (ex) {
    // no-op
  }

  return image;
};

/**
 * PSN and XBox are not direct platforms
 * where user need to enter secondary code on playstation or xbox site
 */
export const isDirectPlatform = (platform) => {
  return !['psn', 'xbl'].includes(platform);
};

/**
 * PSN country code, eg. "us", "ca"
 */

export const getPsnCountry = (externalAuths) => {
  let countryCode = null;

  for (const auth of externalAuths) {
    if (auth.type === 'psn') {
      const {regionInfo} = auth;
      if (regionInfo) {
        countryCode = regionInfo.toLowerCase();
      }
      break;
    }
  }

  return countryCode;
};

export const getSuccessData = (posaRedeemStore) => {
  const {
    platform,
    page: {generalSuccess, psnSuccess, xblSuccess},
    secondaryCode,
  } = posaRedeemStore;
  if (!secondaryCode || !secondaryCode.code) {
    // secondaryCode not found, use general success data
    return generalSuccess;
  }
  switch (platform) {
    case 'mobile':
    case 'pc':
    case 'nitendo':
      return generalSuccess;
    case 'psn':
      return psnSuccess;
    case 'xbl':
      return xblSuccess;
    default:
      return generalSuccess;
  }
};
